import * as client from "../../client";
import { displayNameForUser, isAutomationAccount } from "../../util";
import { LinkSegment, splitLinks } from "./links";

// Matches "@[Display Name](user_id)". Mirrors MENTION_PATTERN in
// backend/pantheon/src/asgard/asgard/comment_mentions.py.
export const MENTION_REGEX = /@\[([^\]]+)\]\(([^)\s]+)\)/g;

export type MessageSegment =
  | LinkSegment
  | { kind: "mention"; name: string; userId: string };

export function splitMessage(message: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;
  for (const match of message.matchAll(MENTION_REGEX)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      segments.push(...splitLinks(message.slice(lastIndex, index)));
    }
    segments.push({
      kind: "mention",
      name: match[1] ?? "",
      userId: match[2] ?? "",
    });
    lastIndex = index + match[0].length;
  }
  if (lastIndex < message.length) {
    segments.push(...splitLinks(message.slice(lastIndex)));
  }
  return segments;
}

// The canonical token for a mention of the given user, as stored in the
// comment message.
export function mentionInsertText(user: client.User): string {
  return `@[${displayNameForUser(user)}](${user.id})`;
}

// What a mention looks like while typing in the textarea. The canonical
// token (with the user id) is substituted in just before saving, so the
// composer never shows raw ids — Slack-style.
export function mentionDisplayText(user: client.User): string {
  return `@${displayNameForUser(user)}`;
}

const escapeRegExp = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Convert display-text mentions ("@Jane Doe") into canonical tokens for the
// given org users. Longer names are matched first so "@Sean Stephenson"
// never partially matches a user named "Sean Stephens".
export function encodeMentions(text: string, users: client.User[]): string {
  const candidates = users
    .filter((user) => !isAutomationAccount(user))
    .map((user) => ({ user, name: displayNameForUser(user) }))
    .filter((candidate) => candidate.name.length > 0)
    .sort((a, b) => b.name.length - a.name.length);
  let encoded = text;
  for (const { user, name } of candidates) {
    // "@" + name, at the start or after whitespace, not followed by more of
    // a longer word.
    const pattern = new RegExp(`(^|\\s)@${escapeRegExp(name)}(?![\\w])`, "gi");
    encoded = encoded.replace(
      pattern,
      (_match, prefix: string) => `${prefix}${mentionInsertText(user)}`
    );
  }
  return encoded;
}

// Convert canonical tokens back to display text for editing.
export function decodeMentions(text: string): string {
  return text.replace(MENTION_REGEX, (_match, name: string) => `@${name}`);
}

export type ActiveMention = {
  // Index of the "@" that starts the mention being typed.
  start: number;
  // Text between the "@" and the caret.
  query: string;
};

// Find the "@query" being typed immediately before the caret, if any. The "@"
// must be at the start of the text or preceded by whitespace, like Slack.
export function getActiveMention(
  value: string,
  caret: number
): ActiveMention | null {
  const beforeCaret = value.slice(0, caret);
  const start = beforeCaret.lastIndexOf("@");
  if (start === -1) {
    return null;
  }
  if (start > 0 && !/\s/.test(beforeCaret[start - 1] ?? "")) {
    return null;
  }
  const query = beforeCaret.slice(start + 1);
  // A newline, an already-completed mention token, or an absurdly long query
  // means the user is past mentioning and just typing.
  if (query.includes("\n") || query.includes("](") || query.length > 50) {
    return null;
  }
  return { start, query };
}

// Replace the active "@query" (through the caret) with the picked user's
// display text, returning the new value and caret position.
export function insertMention(
  value: string,
  mention: ActiveMention,
  caret: number,
  user: client.User
): { value: string; caret: number } {
  const inserted = `${mentionDisplayText(user)} `;
  return {
    value: value.slice(0, mention.start) + inserted + value.slice(caret),
    caret: mention.start + inserted.length,
  };
}

export function filterMentionableUsers(
  users: client.User[],
  query: string,
  excludeUserId: string | undefined
): client.User[] {
  const lowerQuery = query.toLowerCase();
  return users.filter(
    (user) =>
      !isAutomationAccount(user) &&
      user.id !== excludeUserId &&
      ((user.full_name ?? "").toLowerCase().includes(lowerQuery) ||
        user.email.toLowerCase().includes(lowerQuery))
  );
}
